import styled from 'styled-components';
import {graphql, useFragment} from 'react-relay';
import type {ActivityTimelineItem_activity$key} from './__generated__/ActivityTimelineItem_activity.graphql';
import {MessageBubble} from './MessageBubble';
import {EventPill} from './EventPill';
import {CopyLinkButton} from './CopyLinkButton';
import {MentionText} from './MentionText';
import {ticketingAttachmentDownloadUrl} from '../../../graphql/forecastRelay';

// Stacked wrapper (rows of label + card). `.group` exposed the hover state to
// CopyLinkButton via group-hover; the button reads its own hover now, but the
// class is harmless and the `deep-link-highlight` class is a kept index.css
// animation (ACC-523 Task 15).
const StackWrapper = styled.div`
	position: relative;
	> * + * {
		margin-top: 8px;
	}
`;

const Row = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const Chip = styled.span<{$outbound: boolean}>`
	border-radius: ${p => p.theme.ticketing.radius.full};
	border: 1px solid ${p => (p.$outbound ? '#dcd9ff' : '#e2e8f0')};
	background: ${p => (p.$outbound ? '#eeedff' : '#ffffff')};
	padding: 2px 10px;
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.025em;
	color: ${p => (p.$outbound ? '#3d36b3' : '#475569')};
`;

const NoteChip = styled.span`
	border-radius: ${p => p.theme.ticketing.radius.full};
	border: 1px dashed #dcd9ff;
	background: #eeedff;
	padding: 2px 10px;
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.025em;
	color: #3d36b3;
`;

const Timestamp = styled.span`
	font: ${p => p.theme.ticketing.font.body3};
	color: #94a3b8;
`;

const EmailCard = styled.div`
	overflow: hidden;
	border-radius: 8px;
	border: 1px solid #e2e8f0;
	background: #ffffff;
	box-shadow: 0 1px 2px rgb(15 23 42 / 0.06);
`;

const EmailHeader = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	border-bottom: 1px solid #f1f5f9;
	padding: 12px 20px;
`;

const Avatar = styled.div<{$outbound: boolean}>`
	display: flex;
	height: 32px;
	width: 32px;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	border-radius: ${p => p.theme.ticketing.radius.full};
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 500;
	background: ${p => (p.$outbound ? '#dcd9ff' : '#e2e8f0')};
	color: ${p => (p.$outbound ? '#3d36b3' : '#475569')};
`;

const AuthorName = styled.span`
	font: ${p => p.theme.ticketing.font.subheading2};
	font-weight: 500;
	color: #334155;
`;

const CustomerTag = styled.span`
	border-radius: ${p => p.theme.ticketing.radius.full};
	border: 1px solid #e2e8f0;
	background: #f8fafc;
	padding: 2px 8px;
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 500;
	color: #475569;
`;

const NoteCard = styled.div`
	border-radius: 8px;
	border: 1px dashed #dcd9ff;
	background: rgb(238 237 255 / 0.6);
	padding: 16px 20px;
`;

const NoteAuthorRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 8px;
`;

const NoteAvatar = styled.span`
	display: flex;
	height: 28px;
	width: 28px;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	border-radius: ${p => p.theme.ticketing.radius.full};
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 600;
	background: #eeedff;
	color: #3d36b3;
`;

const NoteHint = styled.p`
	margin: 0 0 8px;
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 500;
	color: #4f47d6;
`;

const NoteBody = styled.div`
	font: ${p => p.theme.ticketing.font.subheading2};
	color: #1e293b;
`;

const AttachmentRow = styled.div`
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 4px 0;
`;

const Divider = styled.span`
	height: 1px;
	flex: 1 1 0%;
	background: #f1f5f9;
`;

const AttachmentLink = styled.a`
	display: flex;
	align-items: center;
	gap: 6px;
	border-radius: ${p => p.theme.ticketing.radius.full};
	background: #f8fafc;
	padding: 4px 12px;
	font: ${p => p.theme.ticketing.font.body3};
	color: #475569;
	text-decoration: none;
	&:hover {
		background: #f1f5f9;
	}
`;

const AttachmentName = styled.span`
	font-weight: 500;
`;

const AttachmentSize = styled.span`
	color: #94a3b8;
`;

const EventWrapper = styled.div`
	position: relative;
`;

const EventCopySlot = styled.span`
	position: absolute;
	right: 0;
	top: 50%;
	transform: translateY(-50%);
`;

const TimelineItemFragment = graphql`
	fragment ActivityTimelineItem_activity on ActivityEvent {
		__typename
		... on MessageActivity {
			id
			createdAt
			body
			bodyHtml
			direction
			authorType
			author {
				id
				fullName
			}
			deliveryStatus
			retryCount
			attachments {
				id
				filename
				contentType
				sizeBytes
				inlineContentId
				downloadUrl
			}
		}
		... on InternalCommentActivity {
			id
			createdAt
			body
			author {
				id
				fullName
			}
			mentionedUsers {
				id
				fullName
			}
		}
		... on EventActivity {
			id
			createdAt
			eventType
			actor {
				id
				fullName
			}
			oldValue
			newValue
		}
		... on AttachmentActivity {
			id
			createdAt
			filename
			contentType
			sizeBytes
			downloadUrl
		}
	}
`;

interface Props {
	activity: ActivityTimelineItem_activity$key;
	onRetry?: (messageId: string) => void;
	isHighlighted?: boolean;
}

function formatTime(iso: string): string {
	return new Date(iso).toLocaleString();
}

export function ActivityTimelineItem({activity: activityRef, onRetry, isHighlighted}: Props) {
	const event = useFragment(TimelineItemFragment, activityRef);
	const highlightClass = isHighlighted ? 'deep-link-highlight' : undefined;

	switch (event.__typename) {
		case 'MessageActivity': {
			const isOutbound = event.direction === 'outbound' || event.authorType === 'agent';
			const messageLike = {
				id: event.id,
				event_type: 'message' as const,
				created_at: event.createdAt,
				author_type: (event.authorType === 'agent' ? 'agent' : 'requester') as 'agent' | 'requester',
				author_id: event.author?.id ?? null,
				body: event.body,
				body_html: event.bodyHtml ?? null,
				direction: (event.direction ?? null) as 'inbound' | 'outbound' | null,
				attachments: (event.attachments ?? []).map(a => ({
					id: a.id,
					filename: a.filename,
					size_bytes: a.sizeBytes,
					mime_type: a.contentType,
					inline_content_id: a.inlineContentId ?? null,
					downloadUrl: a.downloadUrl,
				})),
				delivery_status: (event.deliveryStatus ?? null) as 'queued' | 'sending' | 'sent' | 'failed' | null,
				retry_count: event.retryCount,
			};
			const authorLabel = event.author ? event.author.fullName ?? 'Deleted user' : isOutbound ? 'Agent' : 'Requester';
			const authorInitial = authorLabel.charAt(0).toUpperCase();
			return (
				<StackWrapper id={event.id} className={highlightClass}>
					<Row>
						<Chip $outbound={isOutbound}>EMAIL · {isOutbound ? 'SENT' : 'RECEIVED'}</Chip>
						<Timestamp>{formatTime(event.createdAt)}</Timestamp>
						<CopyLinkButton eventId={event.id} />
					</Row>
					<EmailCard data-testid="email-card">
						<EmailHeader>
							<Avatar $outbound={isOutbound}>{authorInitial}</Avatar>
							<AuthorName>{authorLabel}</AuthorName>
							{!isOutbound && <CustomerTag>Customer</CustomerTag>}
						</EmailHeader>
						<MessageBubble event={messageLike} onRetry={onRetry} />
					</EmailCard>
				</StackWrapper>
			);
		}

		case 'InternalCommentActivity': {
			const authorLabel = event.author ? event.author.fullName ?? 'Deleted user' : 'Deleted user';
			const authorInitial = authorLabel.charAt(0).toUpperCase();
			return (
				<StackWrapper id={event.id} className={highlightClass}>
					<Row>
						<NoteChip>INTERNAL NOTE</NoteChip>
						<Timestamp>{formatTime(event.createdAt)}</Timestamp>
						<CopyLinkButton eventId={event.id} />
					</Row>
					<NoteCard data-testid="internal-note-card">
						<NoteAuthorRow>
							<NoteAvatar>{authorInitial}</NoteAvatar>
							<AuthorName>{authorLabel}</AuthorName>
						</NoteAuthorRow>
						<NoteHint>Only visible to your team</NoteHint>
						<NoteBody>
							<MentionText
								body={event.body}
								mentionedUsers={Object.fromEntries(
									(event.mentionedUsers ?? []).map(u => [u.id, u.fullName ?? 'Deleted user'])
								)}
							/>
						</NoteBody>
					</NoteCard>
				</StackWrapper>
			);
		}

		case 'AttachmentActivity': {
			return (
				<AttachmentRow id={event.id} className={highlightClass}>
					<Divider />
					<AttachmentLink
						href={ticketingAttachmentDownloadUrl(event.downloadUrl)}
						target="_blank"
						rel="noopener noreferrer"
					>
						<span>📎</span>
						<AttachmentName>{event.filename}</AttachmentName>
						<AttachmentSize>({(event.sizeBytes / 1024).toFixed(1)} KB)</AttachmentSize>
					</AttachmentLink>
					<CopyLinkButton eventId={event.id} />
					<Timestamp>{formatTime(event.createdAt)}</Timestamp>
					<Divider />
				</AttachmentRow>
			);
		}

		case 'EventActivity': {
			const eventLike = {
				id: event.id,
				event_type: event.eventType,
				created_at: event.createdAt,
				actor_id: event.actor?.id ?? null,
				actor_name: event.actor ? event.actor.fullName ?? 'Deleted user' : null,
				payload:
					event.oldValue != null || event.newValue != null
						? {from: event.oldValue ?? undefined, to: event.newValue ?? undefined}
						: null,
			};
			return (
				<EventWrapper className={highlightClass}>
					<EventPill event={eventLike as Parameters<typeof EventPill>[0]['event']} />
					<EventCopySlot>
						<CopyLinkButton eventId={event.id} />
					</EventCopySlot>
				</EventWrapper>
			);
		}

		default:
			return null;
	}
}
